/**
 * 日報のAIワークスペース — 右サイドパネル（モーダルではない）。
 * AIの入口はここ一箇所のみ。各項目のラベル横の ✨ はこのパネルを
 * その項目に「ねらいを定めて」開くだけで、生成はここで行う。
 */
import { useEffect, useRef, useState } from 'react';
import {
  Sparkle, X, CircleNotch, ClockCounterClockwise, CaretDown, SlidersHorizontal,
  Eye, ArrowUUpLeft, ArrowClockwise, Trash,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import ModelPresetPicker from '@/components/ai/ModelPresetPicker';
import { formatDateTime } from '@/utils/datetime';

const fmtWhen = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch (_e) { return iso; }
};

/** AI処理中の進捗 — サーバーの実ステージ優先、なければ経過時間表示 */
export function AiProgressBar({ busy, stages, serverStage }) {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!busy) { setElapsed(0); return undefined; }
    const t = setInterval(() => setElapsed((v) => v + 0.5), 500);
    return () => clearInterval(t);
  }, [busy]);
  if (!busy) return null;
  const pct = serverStage?.total
    ? Math.min(94, Math.round(100 * (serverStage.current || 0) / serverStage.total))
    : Math.min(94, Math.round(100 * (1 - Math.exp(-elapsed / 15))));
  const stage = serverStage?.label || stages[Math.min(stages.length - 1, Math.floor(elapsed / 10))];
  return (
    <div className="space-y-1.5" data-testid="ai-progress-bar">
      <div className="h-1.5 rounded-full bg-zinc-100 overflow-hidden">
        <div className="h-full rounded-full bg-teal-500 transition-all duration-500" style={{ width: `${pct}%` }} />
      </div>
      <p className="text-xs text-zinc-500 flex items-center gap-1.5">
        <CircleNotch size={12} className="animate-spin" /> {stage}
        <span className="text-zinc-400 tabular-nums ml-auto">{Math.round(elapsed)}秒 · {pct}%</span>
      </p>
    </div>
  );
}


/** result_snapshot は {field: text} で保存される（古い記録は文字列の可能性あり） */
export function parseSnapshot(snap) {
  if (!snap) return null;
  if (typeof snap === 'object') return snap;
  if (typeof snap === 'string') {
    const t = snap.trim();
    if (!t) return null;
    try { return JSON.parse(t); } catch (_e) { /* not JSON */ }
    return { _raw: t };
  }
  return null;
}

const catLabel = (h) => (h.scope && h.scope !== 'full_report' ? (h.field_label || '単一項目') : '全体');

/** 生成履歴の1件 — 行クリックで詳細を開き、表示/再利用/復元/削除ができる */
function HistoryCard({ h, fields, expanded, onToggle, onView, onReuse, onRestore, onDelete }) {
  const snap = parseSnapshot(h.result_snapshot);
  const entries = snap
    ? Object.entries(snap).filter(([, v]) => String(v || '').trim()).map(([k, v]) => ({
      key: k,
      label: k === '_raw' ? '生成結果' : (fields.find((f) => f.key === k)?.label || k),
      text: String(v),
    }))
    : [];
  return (
    <div className={`rounded-lg border bg-white transition-colors ${expanded ? 'border-teal-300' : 'border-zinc-200 hover:border-teal-200'}`}
      data-testid={`report-ai-history-item-${h.id}`}>
      <button type="button" onClick={onToggle} aria-expanded={expanded}
        data-testid={`report-ai-history-${h.id}`}
        className="w-full text-left px-2.5 py-2">
        <span className="flex items-center gap-1.5 text-xs text-zinc-400">
          <CaretDown size={11} weight="bold" className={`text-zinc-400 transition-transform ${expanded ? '' : '-rotate-90'}`} />
          {fmtWhen(h.created_at)}
          {h.user_name ? `· ${h.user_name}` : ''}
          <span className="px-1 rounded bg-zinc-100 text-zinc-600">{catLabel(h)}</span>
          {!snap && <span className="ml-auto text-zinc-300">結果なし</span>}
        </span>
        <span className="block mt-1 text-xs text-zinc-700 max-h-[48px] overflow-hidden whitespace-pre-wrap">{h.notes}</span>
      </button>

      {expanded && (
        <div className="px-2.5 pb-2.5 space-y-2 border-t border-zinc-200 pt-2" data-testid={`report-ai-history-detail-${h.id}`}>
          <dl className="grid grid-cols-[64px_1fr] gap-x-2 gap-y-1 text-xs">
            <dt className="text-zinc-400">生成時刻</dt>
            <dd className="text-zinc-700">{h.created_at ? formatDateTime(h.created_at) : '—'}</dd>
            <dt className="text-zinc-400">区分</dt><dd className="text-zinc-700">{catLabel(h)}</dd>
            <dt className="text-zinc-400">実行者</dt><dd className="text-zinc-700">{h.user_name || '—'}</dd>
            <dt className="text-zinc-400">モデル</dt>
            <dd className="text-zinc-700">{h.model_name || '—'}{h.preset_name ? ` · ${h.preset_name}` : ''}</dd>
            {h.project_name && (<><dt className="text-zinc-400">対象</dt><dd className="text-zinc-700">{h.project_name}</dd></>)}
          </dl>

          <div>
            <p className="text-xs font-medium text-zinc-500">元メモ</p>
            <p className="mt-0.5 rounded-md bg-zinc-50 border border-zinc-200 p-2 text-xs text-zinc-700 whitespace-pre-wrap max-h-40 overflow-y-auto">
              {h.notes || '（メモなし）'}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-zinc-500">生成結果</p>
            {entries.length === 0 ? (
              <p className="mt-0.5 text-xs text-zinc-400">この履歴には生成結果が保存されていません</p>
            ) : entries.map((e) => (
              <div key={e.key} className="mt-1 rounded-md border border-zinc-200 p-2">
                <p className="text-xs text-zinc-500">{e.label}</p>
                <p className="mt-0.5 text-xs text-zinc-800 whitespace-pre-wrap max-h-40 overflow-y-auto">{e.text}</p>
              </div>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-1.5 pt-0.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={entries.length === 0}
              onClick={onView} data-testid={`report-ai-history-view-${h.id}`}>
              <Eye size={13} className="mr-1" /> 表示
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!(h.notes || '').trim()}
              onClick={onReuse} data-testid={`report-ai-history-reuse-${h.id}`}>
              <ArrowUUpLeft size={13} className="mr-1" /> 再利用
            </Button>
            <Button size="sm" className="h-7 text-xs" disabled={entries.length === 0}
              onClick={onRestore} data-testid={`report-ai-history-restore-${h.id}`}>
              <ArrowClockwise size={13} className="mr-1" /> 復元
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600 hover:bg-rose-50 ml-auto"
              onClick={onDelete} data-testid={`report-ai-history-delete-${h.id}`}>
              <Trash size={13} className="mr-1" /> 削除
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EngReportAiPanel({
  open, onClose, fields, target, setTarget, notes, setNotes, aiSel, setAiSel,
  busy, stage, instanceId, onGenerate, preview, onInsertPreview, onInsertPreviewAll,
  suggestion, onApplySuggestion, onDiscardSuggestion, history, canSeeOthers,
  onReuseNotes, onShowResult, onRestoreResult, onDeleteHistory,
}) {
  const [advOpen, setAdvOpen] = useState(false);
  const [histOpen, setHistOpen] = useState(false);
  const [openHistId, setOpenHistId] = useState(null);
  const bodyRef = useRef(null);
  // 表示/再利用は上部のメモ・生成結果を書き換えるため、そこまでスクロールして見せる
  const scrollTop = () => bodyRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  if (!open) return null;
  const targetLabel = target === 'full'
    ? '日報の全項目'
    : fields.find((f) => f.key === target)?.label || '';
  const canGenerate = !busy && notes.trim() && !(aiSel?.selectable && !aiSel?.ready);

  return (
    <>
      <div className="fixed inset-0 z-30 bg-zinc-900/20 lg:hidden" onClick={onClose} aria-hidden />
      <aside
        className="fixed right-0 top-14 sm:top-16 z-40 h-[calc(100%-3.5rem)] sm:h-[calc(100%-4rem)] w-full sm:w-[420px] bg-white border-l border-zinc-200 shadow-xl flex flex-col animate-in slide-in-from-right duration-200"
        data-testid="report-ai-panel">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200">
          <Sparkle size={17} weight="duotone" className="text-teal-600" />
          <div className="min-w-0">
            <p className="text-base font-medium text-zinc-900 leading-tight">AIアシスタント</p>
            <p className="text-xs text-zinc-500 truncate">下書きの相談相手 — 本文は自分の言葉で仕上げてください</p>
          </div>
          <button type="button" onClick={onClose} data-testid="report-ai-panel-close"
            className="ml-auto p-1.5 rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors">
            <X size={16} />
          </button>
        </header>

        <div ref={bodyRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
          <section className="space-y-2">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">生成する対象</p>
            <div className="flex flex-wrap gap-1.5" data-testid="report-ai-target-picker">
              <button type="button" onClick={() => setTarget('full')} data-testid="report-ai-target-full"
                className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                  target === 'full' ? 'bg-teal-600 border-teal-600 text-white'
                    : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}>
                全項目
              </button>
              {fields.map((f) => (
                <button key={f.key} type="button" onClick={() => setTarget(f.key)}
                  data-testid={`report-ai-target-${f.key}`}
                  className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                    target === f.key ? 'bg-teal-600 border-teal-600 text-white'
                      : 'bg-white border-zinc-200 text-zinc-600 hover:border-zinc-300'}`}>
                  {f.label}
                </button>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">メモ（生成の元情報）</p>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={8}
              placeholder={'例:\nカメラのテスト実施\nドライバー不具合あり\nUSB電源の問題と判明\n追加検証が必要'}
              className="text-sm" data-testid="report-ai-notes-input" />
            <AiProgressBar busy={busy} serverStage={stage}
              stages={['メモを解析中…', '各項目へ整理中…', '文章を仕上げ中…（ローカルLLMでは時間がかかる場合があります）']} />
            <Button onClick={onGenerate} disabled={!canGenerate}
              className="w-full h-9 bg-teal-700 hover:bg-teal-800" data-testid="report-ai-generate-btn">
              {busy ? '生成中…' : `${targetLabel}を生成`}
            </Button>
          </section>

          {suggestion && (
            <section className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-3" data-testid="report-ai-suggestion">
              <p className="text-xs font-medium text-zinc-500 flex items-center gap-1">
                {fields.find((f) => f.key === suggestion.field)?.label} の提案
                <span className="ml-auto font-normal text-zinc-400">
                  {suggestion.model_name}{suggestion.preset_name ? ` · ${suggestion.preset_name}` : ''}
                </span>
              </p>
              <p className="mt-1.5 text-sm text-zinc-800 whitespace-pre-wrap leading-relaxed">{suggestion.text}</p>
              <div className="mt-2.5 flex items-center gap-1.5">
                <Button size="sm" onClick={() => onApplySuggestion('insert')}
                  className="h-7 text-xs bg-teal-700 hover:bg-teal-800" data-testid="report-ai-insert-btn">挿入</Button>
                <Button size="sm" variant="outline" onClick={() => onApplySuggestion('replace')}
                  className="h-7 text-xs" data-testid="report-ai-replace-btn">置き換え</Button>
                <Button size="sm" variant="ghost" onClick={onDiscardSuggestion}
                  className="h-7 text-xs text-zinc-500" data-testid="report-ai-discard-btn">破棄</Button>
              </div>
            </section>
          )}

          {preview && (
            <section className="rounded-xl border border-zinc-200 bg-zinc-50/70 p-3 space-y-2" data-testid="report-ai-preview">
              <p className="text-xs font-medium text-zinc-500 flex items-center gap-1">
                生成結果
                <Button size="sm" onClick={onInsertPreviewAll}
                  className="ml-auto h-7 text-xs bg-teal-700 hover:bg-teal-800"
                  data-testid="report-ai-preview-insert-all">全項目を反映</Button>
              </p>
              {fields.filter(({ key }) => (preview[key] || '').trim()).map(({ key, label }) => (
                <div key={key} className="rounded-lg border border-zinc-200 bg-white p-2.5">
                  <p className="text-xs font-medium text-zinc-500 flex items-center gap-1">
                    {label}
                    <button type="button" onClick={() => onInsertPreview(key)}
                      className="ml-auto text-xs font-medium text-teal-700 hover:underline"
                      data-testid={`report-ai-preview-insert-${key}`}>この項目を反映</button>
                  </p>
                  <p className="mt-1 text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{preview[key]}</p>
                </div>
              ))}
            </section>
          )}

          <section className="rounded-xl border border-zinc-200 overflow-hidden">
            <button type="button" onClick={() => setAdvOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors"
              data-testid="report-ai-advanced-toggle">
              <SlidersHorizontal size={13} className="text-zinc-400" />
              <span className="text-sm text-zinc-700">モデル・プリセット</span>
              <CaretDown size={12} className={`ml-auto text-zinc-400 transition-transform ${advOpen ? '' : '-rotate-90'}`} />
            </button>
            {advOpen && (
              <div className="px-3 pb-3 space-y-2" data-testid="report-ai-advanced-body">
                <ModelPresetPicker feature="daily_report_drafting" instanceId={instanceId}
                  onChange={setAiSel} disabled={busy} wrapperClassName="rounded-lg bg-zinc-50 p-2.5" />
                {aiSel && !aiSel.selectable && (
                  <p className="text-xs text-zinc-500" data-testid="report-ai-cloud-note">
                    クラウドモデル（Emergent）を使用中です。ローカルLLMを選ぶ場合は管理者にAI設定の有効化を依頼してください。
                  </p>
                )}
              </div>
            )}
          </section>

          <section className="rounded-xl border border-zinc-200 overflow-hidden">
            <button type="button" onClick={() => setHistOpen((v) => !v)}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors"
              data-testid="report-ai-history-toggle">
              <ClockCounterClockwise size={13} className="text-zinc-400" />
              <span className="text-sm text-zinc-700">生成履歴</span>
              <span className="text-xs text-zinc-400">{history.length}件</span>
              <CaretDown size={12} className={`ml-auto text-zinc-400 transition-transform ${histOpen ? '' : '-rotate-90'}`} />
            </button>
            {histOpen && (
              <div className="px-3 pb-3 space-y-1.5" data-testid="report-ai-history">
                <p className="text-xs text-zinc-400">
                  行をクリックすると詳細（元メモ・生成結果・モデル）を開けます。
                  {canSeeOthers ? '管理者・マネージャーはこの日報の全生成履歴を確認できます。' : ''}
                </p>
                {history.length === 0 && (
                  <p className="text-xs text-zinc-400 py-4 text-center">まだAI生成の履歴はありません</p>
                )}
                {history.map((h) => (
                  <HistoryCard
                    key={h.id}
                    h={h}
                    fields={fields}
                    expanded={openHistId === h.id}
                    onToggle={() => setOpenHistId((v) => (v === h.id ? null : h.id))}
                    onView={() => { onShowResult(parseSnapshot(h.result_snapshot)); scrollTop(); }}
                    onReuse={() => { onReuseNotes(h); scrollTop(); }}
                    onRestore={() => onRestoreResult(parseSnapshot(h.result_snapshot))}
                    onDelete={() => onDeleteHistory(h)}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}
